import nacl from "tweetnacl";
import { sha256 } from "js-sha256";
import { SenderHmacProofInput } from "./../../core/src/types/crypto/senderHmacProofInput";

/**
 * Computes an HMAC-based proof of sender authenticity for the Unlink protocol (browser version).
 *
 * This function derives a shared secret using Curve25519 ECDH, hashes it to derive an HMAC key,
 * constructs a deterministic challenge input, and signs it using WebCrypto's HMAC support.
 *
 * SECURITY NOTES:
 * - ECDH ensures sender and receiver derive the same shared secret
 * - HMAC key is derived by SHA-256 hashing the shared secret
 * - Challenge construction prevents cross-protocol attacks and binds intent
 * - WebCrypto is used to avoid bundling insecure or bloated polyfills
 *
 * @param input - Input object containing message, keys, and context
 * @returns A 32-byte HMAC-SHA256 proof as Uint8Array
 */
export async function createSenderHmacProof({
  message,
  conversationId,
  receiverViewPublicKey,
  senderViewPrivateKey,
  senderNextViewPublicKey,
}: SenderHmacProofInput): Promise<Uint8Array> {
  const encoder = new TextEncoder();

  // === Step 1: Compute ECDH shared secret (Curve25519) ===
  const sharedSecret = nacl.scalarMult(senderViewPrivateKey, receiverViewPublicKey);

  // === Step 2: Derive HMAC key by hashing the shared secret (SHA-256) ===
  const hmacKeyRaw = new Uint8Array(sha256.array(sharedSecret)); // 32 bytes

  // === Step 3: Domain separation tag to avoid collisions with other HMAC uses ===
  const prefix = encoder.encode("sender_proof");

  // === Step 4: Bind the proof to message contents ===
  const messageHash = new Uint8Array(sha256.array(message)); // 32 bytes

  // === Step 5: Construct deterministic challenge ===
  // Concatenates:
  //   - domain prefix
  //   - conversationId (context binding)
  //   - receiver’s public view key (proof recipient)
  //   - sender’s next view key (forward secrecy)
  //   - hashed message content (integrity/authenticity)
  const challenge = new Uint8Array(
    prefix.length +
    conversationId.length +
    receiverViewPublicKey.length +
    senderNextViewPublicKey.length +
    messageHash.length
  );

  let offset = 0;
  challenge.set(prefix, offset); offset += prefix.length;
  challenge.set(conversationId, offset); offset += conversationId.length;
  challenge.set(receiverViewPublicKey, offset); offset += receiverViewPublicKey.length;
  challenge.set(senderNextViewPublicKey, offset); offset += senderNextViewPublicKey.length;
  challenge.set(messageHash, offset); // no increment needed

  // === Step 6: Import HMAC key into WebCrypto ===
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    hmacKeyRaw,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  // === Step 7: Sign the challenge using HMAC-SHA256 ===
  const signature = await crypto.subtle.sign("HMAC", cryptoKey, challenge);

  // === Step 8: Return the proof as a Uint8Array ===
  return new Uint8Array(signature);
}